import moment from "moment";

// Filter records based on various criteria
export const filterRecords = (
  records,
  searchTerm,
  userFilter,
  dateFrom,
  dateTo,
  requestIdFilter,
  recordTypeFilter
) => {
  return records.filter(record => {
    // Search term filter (searches in userName, requestId, clientName, and notes)
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        (record.userName && record.userName.toLowerCase().includes(search)) ||
        (record.requestId && record.requestId.toLowerCase().includes(search)) ||
        (record.clientName && record.clientName.toLowerCase().includes(search)) ||
        (record.notes && record.notes.toLowerCase().includes(search)) ||
        (record.receiptId && record.receiptId.toLowerCase().includes(search));
      if (!matchesSearch) return false;
    }

    // User filter
    if (userFilter && record.userName !== userFilter) {
      return false;
    }

    // Date range filter - use collectionDate instead of inputDateTime
    if (dateFrom) {
      const recordDate = new Date(record.collectionDate);
      const fromDate = new Date(dateFrom);
      if (recordDate < fromDate) return false;
    }

    if (dateTo) {
      const recordDate = new Date(record.collectionDate);
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      if (recordDate > toDate) return false;
    }

    // Request ID filter
    if (requestIdFilter && !record.requestId.toLowerCase().includes(requestIdFilter.toLowerCase())) {
      return false;
    }

    // Record type filter
    if (recordTypeFilter !== 'all' && record.recordType !== recordTypeFilter) {
      return false;
    }

    return true;
  });
};

// Sort records by specified field and order
export const sortRecords = (records, sortBy, sortOrder) => {
  return [...records].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];

    // Handle date sorting
    if (sortBy === 'inputDateTime' || sortBy === 'collectionDate') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    // Handle numeric sorting
    if (sortBy === 'depositAmount') {
      aValue = Number(aValue);
      bValue = Number(bValue);
    }

    // Handle string sorting
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    } else {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    }
  });
};

// Calculate totals by payment method
export const calculatePaymentMethodTotals = (records) => {
  const totals = {};

  records.forEach(record => {
    const method = record.paymentMethod || 'Unknown';
    if (!totals[method]) {
      totals[method] = {
        count: 0,
        total: 0,
        deposits: 0,
        refunds: 0
      };
    }

    totals[method].count++;
    totals[method].total += record.depositAmount;

    if (record.recordType === 'deposit') {
      totals[method].deposits += record.depositAmount;
    } else {
      totals[method].refunds += Math.abs(record.depositAmount);
    }
  });

  return totals;
};

// Calculate totals by user
export const calculateUserTotals = (records) => {
  const totals = {};

  records.forEach(record => {
    const user = record.userName || 'Unknown User';
    if (!totals[user]) {
      totals[user] = {
        count: 0,
        total: 0,
        deposits: 0,
        refunds: 0,
        totalDepositsCash: 0
      };
    }

    totals[user].count++;
    totals[user].total += record.depositAmount;

    if (record.paymentMethod === 'cash') {
      totals[user].totalDepositsCash += record.depositAmount;
    }

    if (record.recordType === 'deposit') {
      totals[user].deposits += record.depositAmount;
    } else {
      totals[user].refunds += Math.abs(record.depositAmount);
    }
  });

  return totals;
};

// Get date range for quick filters
export const getDateRange = (range) => {
  const today = new Date();
  const from = new Date();

  switch (range) {
    case 'today':
      from.setHours(0, 0, 0, 0);
      return {
        from: from.toISOString().split('T')[0],
        to: today.toISOString().split('T')[0]
      };

    case 'yesterday':
      from.setDate(today.getDate() - 1);
      from.setHours(0, 0, 0, 0);
      const yesterday = new Date(from);
      return {
        from: from.toISOString().split('T')[0],
        to: yesterday.toISOString().split('T')[0]
      };

    case 'week':
      from.setDate(today.getDate() - 7);
      return {
        from: from.toISOString().split('T')[0],
        to: today.toISOString().split('T')[0]
      };

    case 'month':
      from.setMonth(today.getMonth() - 1);
      return {
        from: from.toISOString().split('T')[0],
        to: today.toISOString().split('T')[0]
      };

    case 'thisMonth':
      // Set to first day of current month
      from.setDate(2);
      from.setHours(0, 0, 0, 0);
      return {
        from: from.toISOString().split('T')[0],
        to: today.toISOString().split('T')[0]
      };

    case 'lastMonth':
      // First day of last month
      const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 2);
      // Last day of last month
      const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        from: firstDayLastMonth.toISOString().split('T')[0],
        to: lastDayLastMonth.toISOString().split('T')[0]
      };

    case 'quarter':
      from.setMonth(today.getMonth() - 3);
      return {
        from: from.toISOString().split('T')[0],
        to: today.toISOString().split('T')[0]
      };

    default:
      return { from: '', to: '' };
  }
};

// Generate XLSX export data
export const generateXLSXExportData = (records, paymentMethodTotals, userTotals, stats) => {
  const formatDate = (date) => new Date(date);
  const formatCurrency = (amount) => Number(amount);

  // Payment method display names
  const getPaymentMethodDisplayName = (method) => {
    const methodNames = {
      'bankTransfer': 'Bank Transfer',
      'egyptianPost': 'Egyptian Post',
      'wallet': 'Wallet',
      'cash': 'Cash'
    };
    return methodNames[method] || method;
  };

  // Create workbook data structure
  const workbookData = {
    // Records sheet
    Records: records.map(record => ({
      'Request ID': record.requestId,
      'User Name': record.userName,
      'Client Name': record.clientName,
      'Amount (EGP)': formatCurrency(record.depositAmount),
      'Payment Method': getPaymentMethodDisplayName(record.paymentMethod),
      'Collection Date': formatDate(record.collectionDate),
      'Input Date Time': formatDate(record.inputDateTime),
      'Type': record.recordType,
      'Status': record.status,
      'Receipt ID': record.paymentId,
      'Notes': record.notes || ''
    })),
    // Summary sheet
    Summary: [
      { 'Metric': 'Total Records', 'Value': stats.totalRecords },
      { 'Metric': 'Total Deposits (EGP)', 'Value': formatCurrency(stats.totalCollected) },
      { 'Metric': 'Total Refunds (EGP)', 'Value': formatCurrency(stats.totalRefunds) },
      { 'Metric': 'Net Amount (EGP)', 'Value': formatCurrency(stats.netAmount) },
      { 'Metric': 'Active Agents', 'Value': stats.activeAgents },
      { 'Metric': 'Average Deposit (EGP)', 'Value': formatCurrency(stats.averageDeposit) }
    ],

    // Payment Methods sheet
    'Payment Methods': Object.entries(paymentMethodTotals).map(([method, totals]) => ({
      'Payment Method': getPaymentMethodDisplayName(method),
      'Transaction Count': totals.count,
      'Total Amount (EGP)': formatCurrency(totals.total),
      'Deposits (EGP)': formatCurrency(totals.deposits),
      'Refunds (EGP)': formatCurrency(totals.refunds)
    })),

    // User Performance sheet
    'User Performance': Object.entries(userTotals).map(([user, totals]) => ({
      'User Name': user,
      'Transaction Count': totals.count,
      'Total Amount (EGP)': formatCurrency(totals.total),
      'Deposits (EGP)': formatCurrency(totals.deposits),
      'Refunds (EGP)': formatCurrency(totals.refunds)
    }))
  };

  return workbookData;
};

// Generate XML export (keeping for backward compatibility)
// export const generateXMLExport = (records, paymentMethodTotals) => {
//   const formatDate = (date) => new Date(date).toLocaleString();
//   const formatCurrency = (amount) => `${Math.abs(amount).toFixed(2)} EGP`;

//   // Payment method display names for XML export
//   const getPaymentMethodDisplayName = (method) => {
//     const methodNames = {
//       'bankTransfer': 'Bank Transfer',
//       'egyptianPost': 'Egyptian Post',
//       'wallet': 'Wallet',
//       'cash': 'Cash'
//     };
//     return methodNames[method] || method;
//   };

//   let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
//   xml += '<DepositCollectionReport>\n';
//   xml += `  <GeneratedAt>${new Date().toISOString()}</GeneratedAt>\n`;
//   xml += `  <TotalRecords>${records.length}</TotalRecords>\n`;

//   // Summary section
//   xml += '  <Summary>\n';
//   const totalDeposits = records.filter(r => r.recordType === 'deposit').reduce((sum, r) => sum + r.depositAmount, 0);
//   const totalRefunds = Math.abs(records.filter(r => r.recordType === 'refund').reduce((sum, r) => sum + r.depositAmount, 0));
//   xml += `    <TotalDeposits>${formatCurrency(totalDeposits)}</TotalDeposits>\n`;
//   xml += `    <TotalRefunds>${formatCurrency(totalRefunds)}</TotalRefunds>\n`;
//   xml += `    <NetAmount>${formatCurrency(totalDeposits - totalRefunds)}</NetAmount>\n`;
//   xml += '  </Summary>\n';

//   // Payment method totals
//   xml += '  <PaymentMethodTotals>\n';
//   Object.entries(paymentMethodTotals).forEach(([method, totals]) => {
//     xml += `    <PaymentMethod name="${getPaymentMethodDisplayName(method)}">\n`;
//     xml += `      <Count>${totals.count}</Count>\n`;
//     xml += `      <Total>${formatCurrency(totals.total)}</Total>\n`;
//     xml += `      <Deposits>${formatCurrency(totals.deposits)}</Deposits>\n`;
//     xml += `      <Refunds>${formatCurrency(totals.refunds)}</Refunds>\n`;
//     xml += '    </PaymentMethod>\n';
//   });
//   xml += '  </PaymentMethodTotals>\n';

//   // Records section
//   xml += '  <Records>\n';
//   records.forEach(record => {
//     xml += '    <Record>\n';
//     xml += `      <RequestId>${record.requestId}</RequestId>\n`;
//     xml += `      <UserName>${record.userName}</UserName>\n`;
//     xml += `      <clientName>${record.clientName}</clientName>\n`;
//     xml += `      <Amount>${formatCurrency(record.depositAmount)}</Amount>\n`;
//     xml += `      <PaymentMethod>${getPaymentMethodDisplayName(record.paymentMethod)}</PaymentMethod>\n`;
//     xml += `      <CollectionDate>${formatDate(record.collectionDate)}</CollectionDate>\n`;
//     xml += `      <InputDateTime>${formatDate(record.inputDateTime)}</InputDateTime>\n`;
//     xml += `      <Type>${record.recordType}</Type>\n`;
//     xml += `      <Status>${record.status}</Status>\n`;
//     xml += `      <ReceiptId>${record.paymentId}</ReceiptId>\n`;
//     if (record.notes) {
//       xml += `      <Notes>${record.notes}</Notes>\n`;
//     }
//     xml += '    </Record>\n';
//   });
//   xml += '  </Records>\n';
//   xml += '</DepositCollectionReport>';

//   return xml;
// };

